/**
 * Pure URL logic for {@link EmbedFrame}. No React / DOM-component imports so it
 * can be unit-tested in isolation (see `__tests__/embed-url.test.ts`).
 *
 * Two concerns live here:
 *  1. {@link isSafeEmbedUrl} — the safety gate (protocol + self-origin).
 *  2. {@link normalizeEmbedUrl} — rewrite provider "share/watch" URLs to their
 *     embeddable endpoints, because the human-facing page of most providers
 *     refuses to be framed.
 *
 * WHY normalization exists: a normal `https://www.youtube.com/watch?v=…` link
 * responds with `X-Frame-Options: SAMEORIGIN`, so a cross-origin `<iframe>` is
 * blocked and Chrome renders "www.youtube.com refused to connect". YouTube's
 * dedicated player path `https://www.youtube.com/embed/VIDEO_ID` sends NO
 * framing headers and is built to be embedded. Same story for Vimeo, Spotify,
 * Loom, Google Docs, CodePen — each has a share URL that is NOT frameable and a
 * separate embed URL that is. We store whatever the user pasted and rewrite it
 * to the embeddable form at render time. Anything we don't recognize is returned
 * unchanged (and may still refuse to frame — that's the remote site's choice).
 */

/** YouTube video ids are exactly 11 base64url chars. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Whether `url` is safe to load in an {@link EmbedFrame}: a well-formed http(s)
 * URL that is NOT this app's own origin. Rejects `javascript:` / `data:` /
 * `file:` and self-embedding. Use it to gate the frame AND to drive a consumer's
 * "enter a URL" empty / validation state.
 */
export function isSafeEmbedUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return false;
  // Never let the app frame itself (sandbox-escape vector, see EMBED_SANDBOX).
  // Read `location.origin` directly: optional-chaining it would make an
  // undetermined origin compare unequal to everything, so the one URL this
  // check exists to reject would be the one it let through.
  if (parsed.origin === window.location.origin) {
    return false;
  }
  return true;
}

/**
 * Rewrite a recognized provider share/watch URL to its embeddable endpoint.
 * Returns the input (trimmed) unchanged when the URL is malformed or from a
 * provider we don't special-case. Idempotent: an already-embeddable URL is
 * passed through untouched (so player params the user set are preserved).
 */
export function normalizeEmbedUrl(raw: string): string {
  const trimmed = raw.trim();
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return trimmed;
  }
  const transformed =
    toYouTubeEmbed(u) ??
    toVimeoEmbed(u) ??
    toSpotifyEmbed(u) ??
    toLoomEmbed(u) ??
    toGoogleDocsEmbed(u) ??
    toCodePenEmbed(u);
  return transformed ?? trimmed;
}

/** `?t=90` / `90s` / `1m30s` / `1h2m3s` → integer seconds (the player wants `start=`). */
function parseStartSeconds(value: string | null): number | null {
  if (!value) return null;
  if (/^\d+$/.test(value)) return parseInt(value, 10) || null;
  const h = Number(value.match(/(\d+)h/)?.[1] ?? 0);
  const m = Number(value.match(/(\d+)m/)?.[1] ?? 0);
  const s = Number(value.match(/(\d+)s/)?.[1] ?? 0);
  const total = h * 3600 + m * 60 + s;
  return total > 0 ? total : null;
}

function toYouTubeEmbed(u: URL): string | null {
  const host = u.hostname.replace(/^(www|m|music)\./, "");
  if (host !== "youtube.com" && host !== "youtu.be" && host !== "youtube-nocookie.com") {
    return null;
  }
  const base =
    host === "youtube-nocookie.com"
      ? "https://www.youtube-nocookie.com"
      : "https://www.youtube.com";

  // Already an embed URL → leave it (keep any player params the user chose).
  if (/^\/embed\//.test(u.pathname) || u.pathname === "/embed") return u.toString();

  const list = u.searchParams.get("list");
  const start = parseStartSeconds(u.searchParams.get("start") ?? u.searchParams.get("t"));

  let id: string | null = null;
  if (host === "youtu.be") {
    id = u.pathname.split("/").filter(Boolean)[0] ?? null;
  } else {
    // /shorts/ID, /live/ID, /v/ID all carry a plain video id.
    const m = u.pathname.match(/^\/(?:shorts|live|v)\/([^/?#]+)/);
    if (m) id = m[1] ?? null;
    else if (u.pathname === "/watch") id = u.searchParams.get("v");
    else if (u.pathname === "/playlist" && list) {
      return `${base}/embed/videoseries?list=${encodeURIComponent(list)}`;
    }
  }

  if (id && !YOUTUBE_ID.test(id)) id = null;
  if (!id) {
    // No usable video id: only a bare playlist link is still embeddable.
    return list ? `${base}/embed/videoseries?list=${encodeURIComponent(list)}` : null;
  }

  const params = new URLSearchParams();
  if (list) params.set("list", list);
  if (start != null) params.set("start", String(start));
  const qs = params.toString();
  return `${base}/embed/${id}${qs ? `?${qs}` : ""}`;
}

function toVimeoEmbed(u: URL): string | null {
  const host = u.hostname.replace(/^www\./, "");
  if (host === "player.vimeo.com") return u.toString(); // already embeddable
  if (host !== "vimeo.com") return null;

  // The video id is the LAST numeric path segment (channels/groups/albums put
  // the id last); an unlisted link appends a private hash right after it.
  const segs = u.pathname.split("/").filter(Boolean);
  let id: string | null = null;
  let hash: string | null = null;
  for (const [i, seg] of segs.entries()) {
    if (/^\d+$/.test(seg)) {
      id = seg;
      const next = segs[i + 1];
      hash = next && /^[A-Za-z0-9]+$/.test(next) ? next : null;
    }
  }
  if (!id) return null;
  return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ""}`;
}

function toSpotifyEmbed(u: URL): string | null {
  if (u.hostname.replace(/^www\./, "") !== "open.spotify.com") return null;
  let segs = u.pathname.split("/").filter(Boolean);
  if (segs[0] === "embed") return u.toString(); // already embeddable
  if (/^intl-[a-z]{2}$/i.test(segs[0] ?? "")) segs = segs.slice(1); // strip locale
  const [type, id] = segs;
  const TYPES = ["track", "album", "playlist", "episode", "show", "artist"];
  if (!type || !id || !TYPES.includes(type)) return null;
  return `https://open.spotify.com/embed/${type}/${id}`;
}

function toLoomEmbed(u: URL): string | null {
  if (u.hostname.replace(/^www\./, "") !== "loom.com") return null;
  const m = u.pathname.match(/^\/(?:share|embed)\/([A-Za-z0-9]+)/);
  return m ? `https://www.loom.com/embed/${m[1]}` : null;
}

function toGoogleDocsEmbed(u: URL): string | null {
  if (u.hostname.replace(/^www\./, "") !== "docs.google.com") return null;
  const m = u.pathname.match(/^\/(document|spreadsheets|presentation)\/d\/([^/]+)/);
  if (!m) return null;
  // Published (`/d/e/…`) or already-embeddable variants → leave untouched.
  if (u.pathname.includes("/d/e/") || /\/(preview|embed|pub|pubhtml)/.test(u.pathname)) {
    return u.toString();
  }
  return `https://docs.google.com/${m[1]}/d/${m[2]}/preview`;
}

function toCodePenEmbed(u: URL): string | null {
  if (u.hostname.replace(/^www\./, "") !== "codepen.io") return null;
  const m = u.pathname.match(/^\/([^/]+)\/(?:pen|embed)\/([^/]+)/);
  return m ? `https://codepen.io/${m[1]}/embed/${m[2]}` : null;
}
